import React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  children: React.ReactNode;
  metricId: string;
  label: string;
  definition?: string | null;
  window?: { start?: string | null; end?: string | null; preset?: string | null } | null;
  onClose: () => void;
}

const windowLabel = (window: Props['window']) => {
  if (!window) return null;
  if (window.start && window.end) return `${window.start} → ${window.end}`;
  return window.preset?.replace(/_/g, ' ') || null;
};

export function NumberSparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const width = 396;
  const height = 72;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - 6 - ((value - min) / range) * (height - 12);
    return `${x},${y}`;
  }).join(' ');
  return (
    <Box component="svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Metric history" data-testid="number-sparkline"
      sx={{ display: 'block', width: '100%', height: 72, overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </Box>
  );
}

export default function NumberDrawer({ children, metricId, label, definition, window: metricWindow, onClose }: Props) {
  const period = windowLabel(metricWindow);
  const [visible, setVisible] = React.useState(true);
  const closeTimer = React.useRef<number | null>(null);
  const closing = React.useRef(false);
  const requestClose = React.useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    setVisible(false);
    closeTimer.current = window.setTimeout(onClose, 170);
  }, [onClose]);
  React.useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    };
  }, [requestClose]);
  return (
    <Drawer
      anchor="right"
      open={visible}
      onClose={requestClose}
      transitionDuration={{ enter: 210, exit: 170 }}
      SlideProps={{ easing: { enter: 'cubic-bezier(0.16, 1, 0.3, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' } }}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        'data-testid': 'number-drawer',
        sx: {
          width: 460,
          maxWidth: '100vw',
          bgcolor: 'var(--surface)',
          backgroundImage: 'linear-gradient(180deg, var(--surface), var(--surface2))',
          borderLeft: '1px solid var(--line)',
          boxShadow: '-18px 0 48px color-mix(in srgb, var(--ink) 12%, transparent)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' },
        },
      } as any}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(12, 18, 28, 0.34)', backdropFilter: 'blur(1px)', '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' } } } }}
    >
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1, px: 2.5, pt: 2.25, pb: 1.75, bgcolor: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1.5, alignItems: 'start' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, color: 'text.secondary', overflowWrap: 'anywhere' }} data-testid="number-metric-id">
              {metricId}
            </Typography>
            <Typography component="h2" sx={{ mt: 0.5, fontSize: 18, lineHeight: 1.25, fontWeight: 700 }}>{label}</Typography>
          </Box>
          <IconButton aria-label="Close number drawer" size="small" onClick={requestClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        {definition && <Typography sx={{ mt: 0.75, fontSize: 12.5, lineHeight: 1.45, color: 'text.secondary' }}>{definition}</Typography>}
        {period && <Typography sx={{ mt: 0.75, fontSize: 11.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{period}</Typography>}
      </Box>
      <Box sx={{ p: 2.5, display: 'grid', gap: 2, overflowY: 'auto' }}>{children}</Box>
    </Drawer>
  );
}
